import { useQuery } from '@tanstack/react-query';

import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useApiClient, usePersistedSetting } from '@@common';

import { ChangeSetClient, ChangeSetViewModel } from '../api/api';
import { Permissions } from '../common/Permissions';
import useHasPermission from '../common/hooks/useHasPermission';
import AuthContext from './AuthContext';

const CURRENT_CHANGESET_ID_KEY = 'currentChangeSetId';

interface ChangeSetContextValue {
	currentChangeSetId: string | null;
	setCurrentChangeSetId: (id: string | null) => void;
	availableChangeSets: ChangeSetViewModel[];
}

const ChangeSetContext = createContext<ChangeSetContextValue | undefined>(undefined);

export const ChangeSetProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const auth = useContext(AuthContext);
	const changeSetClient = useApiClient(ChangeSetClient);
	const canReadChangeSets = useHasPermission(Permissions.ChangeSetRead);

	// Persist currentChangeSetId to localStorage
	const [currentChangeSetId, setCurrentChangeSetId] = usePersistedSetting<string | null>(CURRENT_CHANGESET_ID_KEY, null);

	// Fetch available changeSets
	const { data: changeSetsData } = useQuery({
		queryKey: ['changeSets', 'all'],
		queryFn: () => changeSetClient.search(undefined, undefined, 0, 500, undefined, undefined),
		enabled: auth.isAuthenticated && canReadChangeSets,
	});

	const availableChangeSets = useMemo(() => canReadChangeSets ? changeSetsData?.items ?? [] : [], [canReadChangeSets, changeSetsData]);

	// Auto-select first changeSet if current selection is not in the available list
	useEffect(() => {
		if (availableChangeSets.length === 0) return;

		// If no changeSet selected or current selection not in available list, select first one
		const isCurrentValid = currentChangeSetId && availableChangeSets.some((cs) => cs.id === currentChangeSetId);
		if (!isCurrentValid) {
			setCurrentChangeSetId(availableChangeSets[0].id);
		}
	}, [currentChangeSetId, availableChangeSets, setCurrentChangeSetId]);

	const contextValue = useMemo(
		() => ({
			currentChangeSetId,
			setCurrentChangeSetId,
			availableChangeSets,
		}),
		[currentChangeSetId, setCurrentChangeSetId, availableChangeSets],
	);

	return <ChangeSetContext.Provider value={contextValue}>{children}</ChangeSetContext.Provider>;
};

/**
 * Hook to access the current changeSet context.
 * Provides the currently selected changeSet ID, a function to update it,
 * and the list of available changeSets.
 * All components using this hook will re-render when the changeSet changes.
 */
export function useChangeSet(): ChangeSetContextValue {
	const context = useContext(ChangeSetContext);
	if (context === undefined) {
		throw new Error('useChangeSet must be used within a ChangeSetProvider');
	}
	return context;
}
